import { todayStr } from "../../lib/date";
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { updateStudentSchedule, getAlternatePlans, createAlternatePlan, updateAlternatePlan, deleteAlternatePlan, getBlockSchedules } from "../../lib/api";
import StudentSignupsCard from "./StudentSignupsCard";
import { StudentClassScheduleSection } from "./StudentClassScheduleSection";
import AltPlanFields from "./AltPlanFields";
import StudentScheduleCalendarPrint from "./StudentScheduleCalendarPrint";
import { describePlanSchedule, describePlanTimes } from "../../lib/altPlans";

const DEFAULT_SCHEDULE = {
  monday: { expected: true, start: "08:30", end: "14:00" },
  tuesday: { expected: true, start: "08:30", end: "14:00" },
  wednesday: { expected: true, start: "08:30", end: "14:00" },
  thursday: { expected: true, start: "08:30", end: "14:00" },
  friday: { expected: true, start: "08:30", end: "12:00" },
};

const STANDARD_FULL_START = "08:30";
const STANDARD_FULL_END = "14:00";

// Classify a scheduled day by BOTH its start and end times. A "Full Day" must
// begin at/before the standard start AND end at/after the standard dismissal —
// a late arrival (e.g. 12:10 start) or an early release is never a Full Day.
// HH:MM strings compare lexicographically.
function dayTypeBadge(start, end) {
  const s = (start || STANDARD_FULL_START).trim();
  const e = (end || STANDARD_FULL_END).trim();
  // Afternoon-only arrival or a day that ends at/before noon is a Half Day.
  if (s >= "12:00" || e <= "12:00") return { label: "Half Day", cls: "bg-amber-100 text-amber-800" };
  const lateStart = s > STANDARD_FULL_START;
  const earlyEnd = e < STANDARD_FULL_END;
  if (lateStart && earlyEnd) return { label: "Partial Day", cls: "bg-orange-100 text-orange-800" };
  if (lateStart) return { label: "Late Start", cls: "bg-orange-100 text-orange-800" };
  if (earlyEnd) return { label: "Early Release", cls: "bg-orange-100 text-orange-800" };
  return { label: "Full Day", cls: "bg-green-100 text-green-800" };
}

function scheduledEndFor(student, dateStr) {
  const wd = new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const day = (student?.schedule || {})[wd] || DEFAULT_SCHEDULE[wd];
  return (day?.end || STANDARD_FULL_END);
}

// Attendance records logged with an early release (explicit left-early status OR a
// check-out earlier than the scheduled end) must read as "Early Release", not present.
function attendanceDisplayStatus(att, student) {
  if (att.status === "left_early_unexcused") return "Early Release";
  const co = (att.check_out_time || "").trim();
  if (co && co < scheduledEndFor(student, att.date) && !String(att.status || "").includes("absent")) {
    return "Early Release";
  }
  return att.status;
}

export function ScheduleTab({ student, setStudent, attendance, studentId }) {
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(
    student.schedule && Object.keys(student.schedule).length > 0 
      ? student.schedule 
      : { ...DEFAULT_SCHEDULE }
  );

  const getScheduleForDay = (day) => {
    const schedule = student?.schedule || {};
    return schedule[day] || DEFAULT_SCHEDULE[day];
  };

  const handleSaveSchedule = async () => {
    try {
      const { _effective_date, ...partial } = editingSchedule;
      const effectiveDate = _effective_date || todayStr();
      // Always persist all five weekdays (fill untouched days from defaults) so a
      // partial schedule can never be stored — otherwise missing weekdays would be
      // treated as unset/default elsewhere (e.g. the morning attendance alert).
      const scheduleData = {};
      ["monday", "tuesday", "wednesday", "thursday", "friday"].forEach((day) => {
        scheduleData[day] = { ...DEFAULT_SCHEDULE[day], ...(partial[day] || {}) };
      });
      await updateStudentSchedule(studentId, { ...scheduleData, schedule_effective_date: effectiveDate });
      setStudent(prev => ({ ...prev, schedule: scheduleData, schedule_effective_date: effectiveDate }));
      setScheduleDialogOpen(false);
      toast.success("Schedule updated successfully");
    } catch (err) {
      toast.error("Failed to update schedule");
    }
  };

  return (
    <div className="space-y-6">
      <StudentSignupsCard studentId={studentId} />
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current Schedule</CardTitle>
              <CardDescription>Default: M-Th full days, Fridays half days. First Wednesday of month is half day.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
            <StudentScheduleCalendarPrint student={student} studentId={studentId} />
            <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="edit-schedule-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                  </svg>
                  Edit Schedule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Student Schedule</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Schedule Effective Date <span className="text-xs text-muted-foreground">(defaults to today if blank - won&apos;t affect historical data)</span></Label>
                    <Input 
                      type="date" 
                      value={editingSchedule._effective_date || todayStr()}
                      onChange={(e) => setEditingSchedule(p => ({ ...p, _effective_date: e.target.value }))}
                      data-testid="schedule-effective-date"
                    />
                  </div>
                  {["monday", "tuesday", "wednesday", "thursday", "friday"].map((day) => (
                    <div key={day} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border">
                      <div className="flex items-center space-x-2 min-w-[140px]">
                        <Checkbox
                          id={`sched-${day}`}
                          checked={editingSchedule[day]?.expected ?? true}
                          onCheckedChange={(checked) => 
                            setEditingSchedule((p) => ({
                              ...p,
                              [day]: { ...p[day], expected: checked }
                            }))
                          }
                        />
                        <Label htmlFor={`sched-${day}`} className="capitalize font-medium">{day}</Label>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 flex-1">
                        <Label className="text-sm text-muted-foreground">Start:</Label>
                        <Input
                          type="time"
                          value={editingSchedule[day]?.start || "08:30"}
                          onChange={(e) => 
                            setEditingSchedule((p) => ({
                              ...p,
                              [day]: { ...p[day], start: e.target.value }
                            }))
                          }
                          className="w-28 sm:w-32"
                          disabled={!editingSchedule[day]?.expected}
                        />
                        <Label className="text-sm text-muted-foreground">End:</Label>
                        <Input
                          type="time"
                          value={editingSchedule[day]?.end || (day === "friday" ? "12:00" : "14:00")}
                          onChange={(e) => 
                            setEditingSchedule((p) => ({
                              ...p,
                              [day]: { ...p[day], end: e.target.value }
                            }))
                          }
                          className="w-28 sm:w-32"
                          disabled={!editingSchedule[day]?.expected}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveSchedule} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-schedule-btn">
                      Save Schedule
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {["monday", "tuesday", "wednesday", "thursday", "friday"].map((day) => {
              const schedule = getScheduleForDay(day);
              return (
                <div key={day} className={`text-center p-4 rounded-lg border ${!schedule.expected ? "bg-gray-100 opacity-60" : ""}`}>
                  <h4 className="font-medium mb-2 capitalize">{day}</h4>
                  {schedule.expected ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        {schedule.start || "08:30"} - {schedule.end || "14:00"}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded mt-2 inline-block ${dayTypeBadge(schedule.start, schedule.end).cls}`}>
                        {dayTypeBadge(schedule.start, schedule.end).label}
                      </span>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Not Expected</p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Attendance History */}
      <Card>
        <CardHeader>
          <CardTitle>Attendance History</CardTitle>
        </CardHeader>
        <CardContent>
          {attendance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">Check In</th>
                    <th className="text-left py-2 px-3">Check Out</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[...attendance].sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 20).map((att, i) => (
                    <tr key={att.id || `${att.date}-${i}`} className="border-b">
                      <td className="py-2 px-3">{att.date}</td>
                      <td className="py-2 px-3">{att.check_in_time || "-"}</td>
                      <td className="py-2 px-3">{att.check_out_time || "-"}</td>
                      <td className="py-2 px-3">
                        {(() => { const disp = attendanceDisplayStatus(att, student); return (
                        <span className={`px-2 py-1 rounded text-xs ${
                          disp === "Early Release" ? "bg-orange-100 text-orange-800" :
                          att.status === "present" ? "badge-present" :
                          att.status === "absent" ? "badge-absent" : "badge-tardy"
                        }`} data-testid={`attendance-status-${att.date}`}>
                          {disp}
                        </span> ); })()}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground" data-testid={`attendance-notes-${att.date}`}>{att.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No attendance records</p>
          )}
        </CardContent>
      </Card>

      {/* Student Class Schedule from Master Schedule */}
      <StudentClassScheduleSection studentId={studentId} />

      {/* Alternate Plans (Internships/Jobs) */}
      <AlternatePlansSection studentId={studentId} />
    </div>
  );
}

const EMPTY_ALT_FORM = { plan_type: "weekly", days: [], dates: [], start_date: "", end_date: "", arrival_time: "", departure_time: "", destination: "", notes: "", full_day_out: false };

function AlternatePlansSection({ studentId }) {
  const [plans, setPlans] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_ALT_FORM });

  useEffect(() => {
    fetchPlans();
    fetchBlocks();
  }, [studentId]);

  const fetchPlans = async () => {
    try {
      const res = await getAlternatePlans({ student_id: studentId });
      setPlans(res.data);
    } catch { /* silent */ }
  };

  const fetchBlocks = async () => {
    try {
      const res = await getBlockSchedules();
      const fullBlocks = res.data?.full_day?.blocks || [];
      setBlocks(fullBlocks);
    } catch { /* silent */ }
  };

  const handleSave = async () => {
    const isDates = form.plan_type === "dates";
    const isRange = form.plan_type === "range";
    const hasWindow = form.arrival_time || form.departure_time;
    const fullOut = !!form.full_day_out;
    const scheduleMissing = isRange ? (!form.start_date || !form.end_date) : isDates ? !form.dates.length : !form.days.length;
    if (scheduleMissing || (!fullOut && !hasWindow) || !form.destination) {
      toast.error(isRange ? "Please pick a start and end date, a destination, and an arrival/departure time (unless full day out)" : isDates ? "Please add dates, a destination, and an arrival/departure time (unless full day out)" : "Please fill in days, a destination, and an arrival/departure time (unless full day out)");
      return;
    }
    if (isRange && form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("End date must be on or after the start date");
      return;
    }
    if (!fullOut && form.arrival_time && form.departure_time && form.departure_time <= form.arrival_time) {
      toast.error("Departure time must be after arrival time");
      return;
    }
    try {
      if (editingPlan) {
        await updateAlternatePlan(editingPlan.id, form);
        toast.success("Alternate plan updated");
      } else {
        await createAlternatePlan({ ...form, student_id: studentId });
        toast.success("Alternate plan created");
      }
      setDialogOpen(false);
      setEditingPlan(null);
      setForm({ ...EMPTY_ALT_FORM });
      fetchPlans();
    } catch { toast.error("Failed to save alternate plan"); }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setForm({ plan_type: ["dates", "range"].includes(plan.plan_type) ? plan.plan_type : "weekly", days: plan.days || [], dates: plan.dates || [], start_date: plan.start_date || "", end_date: plan.end_date || "", arrival_time: plan.arrival_time || "", departure_time: plan.departure_time || plan.effective_departure || "", destination: plan.destination, notes: plan.notes || "", full_day_out: !!plan.full_day_out });
    setDialogOpen(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Delete this alternate plan?")) return;
    try { await deleteAlternatePlan(planId); toast.success("Deleted"); fetchPlans(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleDeactivate = async (plan) => {
    try { await updateAlternatePlan(plan.id, { active: !plan.active }); fetchPlans(); }
    catch { toast.error("Failed to update"); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Alternate Plans</CardTitle>
            <CardDescription>Internships, jobs, or off-campus activities during school hours</CardDescription>
          </div>
          <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => { setEditingPlan(null); setForm({ ...EMPTY_ALT_FORM }); setDialogOpen(true); }} data-testid="add-alternate-plan-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
            Add Plan
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {plans.length === 0 ? (
          <p className="text-center py-6 text-muted-foreground">No alternate plans. Add one for internships or off-campus activities.</p>
        ) : (
          <div className="space-y-3">
            {plans.map(plan => (
              <div key={plan.id} className={`p-4 rounded-lg border ${plan.active ? 'bg-amber-50/50 border-amber-200' : 'bg-gray-50 border-gray-200 opacity-60'}`} data-testid={`alt-plan-${plan.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm flex items-center gap-2">
                      {plan.destination}
                      {plan.full_day_out && (
                        <span className="inline-block rounded-full bg-[#00305E] text-white text-[10px] font-medium px-2 py-0.5" data-testid={`alt-plan-fullout-badge-${plan.id}`}>
                          Full day out
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      <span className="font-medium">{describePlanSchedule(plan)}</span>
                      {plan.full_day_out ? " · Out of building all day (excused)" : ` · ${describePlanTimes(plan)}`}
                    </p>
                    {!plan.full_day_out && (plan.missed_block_labels || []).length > 0 && (
                      <p className="text-xs text-amber-700 mt-0.5">Misses {plan.missed_block_labels.join(", ")}</p>
                    )}
                    {plan.notes && <p className="text-xs text-muted-foreground mt-1">{plan.notes}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleDeactivate(plan)} data-testid={`alt-plan-toggle-${plan.id}`}>{plan.active ? "Pause" : "Activate"}</Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleEdit(plan)} data-testid={`alt-plan-edit-${plan.id}`}>Edit</Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500" onClick={() => handleDelete(plan.id)} data-testid={`alt-plan-delete-${plan.id}`}>Delete</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPlan ? "Edit" : "Add"} Alternate Plan</DialogTitle></DialogHeader>
          <div className="pt-2">
            <AltPlanFields form={form} setForm={setForm} blocks={blocks} testidPrefix="alt-" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleSave} data-testid="save-alt-plan-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
